/**
 * Email Activity API Client
 *
 * Provides access to email activity audit endpoints for IT transparency.
 * All team members can access these endpoints to view email operations
 * performed by the platform.
 */

import { getHeaders, PURCHASER_API_BASE_URL } from "../Constants";

// Types for API responses

export type EmailActivityOperationType =
    | "CONNECT_ACCOUNT"
    | "DISCONNECT_ACCOUNT"
    | "FETCH_ACCOUNT_INFO"
    | "FETCH_MAILBOXES"
    | "FETCH_EMAIL_LIST"
    | "FETCH_EMAIL_CONTENT"
    | "FETCH_RAW_EMAIL"
    | "DOWNLOAD_ATTACHMENT"
    | "PARSE_EMAIL_HEADERS"
    | "FETCH_SIGNATURES"
    | "SEND_EMAIL"
    | "FETCH_OAUTH_TOKEN";

export type EmailActivityCategory = "ACCOUNT_MANAGEMENT" | "READ_METADATA" | "READ_CONTENT" | "WRITE" | "OAUTH";

export type EmailActivityActorType = "USER" | "SYSTEM_AUTOMATED" | "SYSTEM_WEBHOOK";

export type EmailActivityRecord = {
    id: number;
    timestamp: string;
    operation: {
        type: EmailActivityOperationType;
        category: EmailActivityCategory;
        success: boolean;
        errorMessage: string | null;
    };
    actor: {
        type: EmailActivityActorType;
        userId: number | null;
        userName: string | null;
        userEmail: string | null;
        userFirstName: string | null;
        userIconBackgroundColorClass: string | null;
        systemProcessName: string | null;
    };
    target: {
        emailEngineAccountId: string;
        senderEmail: string | null;
        targetEmail: string | null;
    };
    metadata: Record<string, unknown> | null;
    requestId: string | null;
};

export type ActivityListResponse = {
    data: EmailActivityRecord[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
};

export type ActivitySummaryResponse = {
    summary: {
        totalOperations: number;
        dateRange: {
            from: string;
            to: string;
        };
        byCategory: Record<string, number>;
        byActorType: Record<string, number>;
        bySuccess: {
            success: number;
            failure: number;
        };
        connectedAccountsActive: number;
    };
    recentActivity: EmailActivityRecord[];
};

export type DataAccessReport = {
    reportGeneratedAt: string;
    team: {
        id: number;
        name: string;
        createdAt: string;
    };
    reportPeriod: {
        days: number;
        from: string;
        to: string;
    };
    summary: {
        totalOperations: number;
        successRate: string;
        failureCount: number;
        connectedAccountCount: number;
        activeUsersCount: number;
    };
    connectedAccounts: Array<{
        id: number;
        email: string;
        connectedAt: string;
        connectedBy: {
            id: number;
            name: string;
            email: string;
        };
        operationCount: number;
    }>;
    operationBreakdown: {
        byType: Array<{
            operationType: EmailActivityOperationType;
            count: number;
        }>;
        byCategory: Array<{
            category: EmailActivityCategory;
            count: number;
        }>;
    };
    actorBreakdown: {
        userActivity: Array<{
            userId: number;
            userName: string | null;
            userEmail: string | null;
            operationCount: number;
        }>;
        systemActivity: Array<{
            processName: string;
            actorType: EmailActivityActorType;
            operationCount: number;
        }>;
    };
    recentFailures: Array<{
        id: number;
        timestamp: string;
        operationType: EmailActivityOperationType;
        errorMessage: string | null;
        user: {
            id: number;
            name: string;
            email: string;
        } | null;
        systemProcessName: string | null;
    }>;
};

export type ActivityQueryParams = {
    page?: number;
    limit?: number;
    // Multi-select filters: comma-separated strings for API
    operationType?: string; // Can be single value or comma-separated
    operationCategory?: string; // Can be single value or comma-separated
    actorType?: string; // Can be single value or comma-separated
    startDate?: string;
    endDate?: string;
    success?: boolean;
    emailEngineAccountId?: string;
};

/**
 * Convert query params object to URL query string
 */
const toQueryString = (params: ActivityQueryParams): string => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
        }
    });
    return searchParams.toString();
};

export default {
    /**
     * Fetch user's own email activity (paginated)
     */
    fetchUserActivity: (params: ActivityQueryParams = {}): Promise<ActivityListResponse> =>
        fetch(`${PURCHASER_API_BASE_URL}/email-activity?${toQueryString(params)}`, {
            headers: getHeaders(),
            method: "GET",
        }).then(res => res.json()),

    /**
     * Fetch team-wide email activity (all team members can access)
     */
    fetchTeamActivity: (params: ActivityQueryParams = {}): Promise<ActivityListResponse> =>
        fetch(`${PURCHASER_API_BASE_URL}/email-activity/team?${toQueryString(params)}`, {
            headers: getHeaders(),
            method: "GET",
        }).then(res => res.json()),

    /**
     * Fetch team activity summary with statistics
     */
    fetchTeamSummary: (): Promise<ActivitySummaryResponse> =>
        fetch(`${PURCHASER_API_BASE_URL}/email-activity/team/summary`, {
            headers: getHeaders(),
            method: "GET",
        }).then(res => res.json()),

    /**
     * Export team activity as CSV
     * Returns a blob that can be downloaded
     */
    exportTeamActivity: async (params: ActivityQueryParams = {}): Promise<Blob> => {
        const response = await fetch(`${PURCHASER_API_BASE_URL}/email-activity/team/export?${toQueryString(params)}`, {
            headers: getHeaders(),
            method: "GET",
        });
        return response.blob();
    },

    /**
     * Fetch comprehensive data access report for IT review
     * Provides detailed breakdown of all email operations for compliance
     */
    fetchDataAccessReport: (): Promise<DataAccessReport> =>
        fetch(`${PURCHASER_API_BASE_URL}/email-activity/team/report`, {
            headers: getHeaders(),
            method: "GET",
        }).then(res => res.json()),
};
